import { Checkbox } from '@creditornot/cb-checkbox';
import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { MenuAccordionCheckboxItem, MenuActionItem } from '@creditornot/cb-menu';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { useCallback, useMemo, forwardRef } from 'react';
import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';
import { isDefined } from 'common/utils';

import { HierarchyOptionT } from './HierarchyOption';
import { Option } from './HierarchyOptions';
import { groupValuesByHierarchy, isDescendantOfNode, removeValueFromHierarchy } from './utils';

type SelectableBranchMenuItemProps = {
  accordionContent: ReactNode | undefined;
  caretPosition?: 'left' | 'right';
  hierarchyOption: HierarchyOptionT;
  isOpen: boolean;
  onAccordionItemClick: () => void;
  onChange: (value: string, values: string[]) => void;
  optionsByIdMap: Map<string, Option>;
  checked: boolean;
  values: string[];
  valuesPath: string[][];
};

const SecondaryText = styled(T.Small)<{ selected?: boolean }>`
  margin-inline-start: 8px;
  color: ${({ selected }) =>
    selected
      ? cssThemedValue({
          light: colors.salt84,
          dark: colors.pepper84,
        })
      : cbSemanticCssVars.textSecondary};
`;

const StyledMenuAccordionCheckboxItem = React.memo(styled(MenuAccordionCheckboxItem)<{ title: string }>``);

const StyledMenuActionItem = styled(MenuActionItem)<{ title: string }>``;

const SelectableBranchMenuItem = forwardRef<HTMLButtonElement, SelectableBranchMenuItemProps>(
  (
    {
      accordionContent,
      caretPosition,
      hierarchyOption,
      isOpen,
      onAccordionItemClick,
      onChange,
      optionsByIdMap,
      values,
      valuesPath,
      checked,
    },
    ref,
  ) => {
    const partiallyChecked = useMemo(
      () => valuesPath.some(paths => paths.includes(hierarchyOption.value)),
      [hierarchyOption.value, valuesPath],
    );

    const handleItemClick = useCallback(() => {
      const someValuesAreHierarchyChild = values.some(value =>
        isDescendantOfNode(hierarchyOption.value, value, optionsByIdMap),
      );
      const [newValues] = [
        values.includes(hierarchyOption.value) ? values.filter(v => v !== hierarchyOption.value) : undefined,
        someValuesAreHierarchyChild
          ? removeValueFromHierarchy(hierarchyOption.value, values, optionsByIdMap)
          : groupValuesByHierarchy([...values, hierarchyOption.value], optionsByIdMap),
      ].filter(isDefined);

      onChange(hierarchyOption.value, newValues);
    }, [values, hierarchyOption.value, optionsByIdMap, onChange]);

    if (!hierarchyOption.children.length) {
      return (
        <StyledMenuActionItem title={hierarchyOption.label} key={hierarchyOption.value} variant="black" ref={ref}>
          <Checkbox readOnly checked={checked} onClick={handleItemClick}>
            {hierarchyOption.label}
            {!!hierarchyOption.secondaryContent && <SecondaryText>({hierarchyOption.secondaryContent})</SecondaryText>}
          </Checkbox>
        </StyledMenuActionItem>
      );
    }

    return (
      <StyledMenuAccordionCheckboxItem
        title={hierarchyOption.label}
        key={hierarchyOption.value}
        open={isOpen}
        onClick={onAccordionItemClick}
        menuItemRef={ref}
        caretPosition={caretPosition}
        checkboxProps={{
          partiallyChecked,
          checked,
          readOnly: true,
          onClick: handleItemClick,
        }}
        accordionContent={isOpen && accordionContent}
      >
        {hierarchyOption.label}
        {!!hierarchyOption.secondaryContent && <SecondaryText>({hierarchyOption.secondaryContent})</SecondaryText>}
      </StyledMenuAccordionCheckboxItem>
    );
  },
);

export default React.memo(SelectableBranchMenuItem);
